/**
 * Search results page controller — /search（首頁搜尋的結果頁）
 *
 * 首頁 hero 搜尋列接線已存在（index.html 的 <form action="search.html" method="get"> + input name=q），
 * 原生 GET submit 即導 search.html?q=值。本控制器只負責「結果頁本身」的三件事：
 *   1. 回顯：把 URL ?q= 的關鍵字寫回搜尋框 input.value（textContent/賦值，禁 innerHTML），
 *      並寫計數行「找到 N 筆與「<q>」相關的結果」（q 空 → 清空計數行，不另顯提示句）。
 *   2. 關鍵字過濾（client-side）：以 q（去空白、小寫、合併連續空白）比對每張結果卡的
 *      可見文字（li.textContent，含標題 + 內文 + tags + 地點），命中才顯示、未命中藏卡 +
 *      隱藏該組「查看全部」CTA。q 空 → 不過濾（全顯）。計數 = 命中數，0 命中觸發空態。
 *   3. tab 委派（選項 A，自寫）：點 .tabs__item → 依 data-filter 顯隱三個結果 section
 *      （all 全顯）+ 切 .is-active / aria-selected；不接 list-filter.js 的 .filter-chip 選擇器。
 *      過濾（li.hidden）與 tab 切類型（group.hidden）正交：計數兩者皆排除。
 *   4. 可見卡 = 0 時顯示 .empty-state（取消 hidden），否則維持 hidden。
 *
 * 設計：root 守門早退（比照 products.js），找不到 tab 列就 return，非該頁不執行、不報錯。
 *       cleanup 走 pagehide（bfcache 安全）。
 *
 * 卡片為靜態 demo HTML（真資料 / AI 摘要待後端）；本控制器在前端對既有卡做純文字關鍵字過濾，
 * 讓搜亂碼回 0 筆 + 觸發空態，搜實詞只留命中卡，計數誠實反映 q（取代「搜任何字都找到 9 筆」）。
 *
 * 分頁（§7 功能化）：搜尋結果為「每類 3 卡預覽 + 各自『查看全部 →』CTA」的分面預覽模型，
 * 非單一長列表。故分頁邏輯 = 對「當前 tab 可見卡集合」算頁數：≤1 頁時 pager 清空（單頁不顯），
 * 取代原靜態假頁碼（1 2 3 4）。共用 buildPaginationItems / renderItemLink（與全站同源省略號）。
 * 切 tab → 可見集合變 → pager 重算。資料量成長到跨頁時自動顯示真分頁（client 切片同 articles）。
 */

import { buildPaginationItems, renderItemLink, renderCompact } from '../components/pagination.js';

const COMPACT_MQ = window.matchMedia ? window.matchMedia('(max-width: 640px)') : null;

const SELECTORS = {
  tabs: '.search-page__tabs',
  tab: '.tabs__item',
  input: '[data-search-input]',
  summary: '[data-search-summary]',
  group: '.search-results__group',   // 三個 tabpanel section（依 data-result-type 對應）
  empty: '[data-search-empty]',
  emptyTitle: '[data-search-empty-title]',
  pager: '.search-page__pagination',
};

const PAGE_SIZE = 9; // 預覽模型每頁上限；9 卡（全部 tab）剛好單頁，pager 清空（誠實）。

// data-filter → 對應結果 section 的 id（all = 全顯）
const FILTER_TO_SECTION = {
  articles: 'results-articles',
  clinics: 'results-clinics',
  products: 'results-products',
};

function init() {
  const tabsRoot = document.querySelector(SELECTORS.tabs);
  if (!tabsRoot) return null;   // 非搜尋頁，早退

  const input = document.querySelector(SELECTORS.input);
  const summary = document.querySelector(SELECTORS.summary);
  const emptyEl = document.querySelector(SELECTORS.empty);
  const emptyTitle = document.querySelector(SELECTORS.emptyTitle);
  const groups = Array.from(document.querySelectorAll(SELECTORS.group));
  const tabs = Array.from(tabsRoot.querySelectorAll(SELECTORS.tab));

  // 全部結果卡（跨三組）。過濾以 li.hidden 標記命中與否（與 tab 的 group.hidden 正交）。
  const allCards = groups.flatMap((group) =>
    Array.from(group.querySelectorAll(':scope > .search-results__grid > li')));

  /* ===== 1. 回顯 q：input value ===== */
  const initialQ = (new URLSearchParams(location.search).get('q') || '').trim();
  // 比對用正規化鍵：小寫 + 合併連續空白（含全形空白），讓大小寫 / 多空白不影響命中。
  const normalize = (s) => String(s).toLowerCase().replace(/\s+/g, ' ').trim();

  if (input && initialQ) {
    // 賦值 .value（DOM property，瀏覽器自動視為純文字，無 HTML 解析）→ 不需 innerHTML、無 XSS 面。
    input.value = initialQ;
  }

  /* ===== 2. 關鍵字過濾：比對每張卡可見文字，命中才顯示 ===== */
  // q 空（直接進 /search 無關鍵字 / 清空輸入框）→ 不過濾，全顯。非空 → 未命中卡 li.hidden=true。
  // q 由呼叫端傳入：載入時用 URL ?q=（既有行為），輸入框打字時用 input.value（即時過濾）。
  // TODO(backend): 改打 GET /search?q= 真檢索（跨文章/診所/產品 + server 分頁）取代靜態卡前端過濾 — 見 docs/BACKEND_HANDOFF.md#site-search
  function applyFilter(q) {
    const needle = normalize(q);
    if (needle === '') {
      allCards.forEach((li) => { li.hidden = false; });
      return;
    }
    allCards.forEach((li) => {
      li.hidden = !normalize(li.textContent).includes(needle);
    });
  }

  /** 計算當前可見結果卡數：排除被 tab 隱藏的 section 與被關鍵字過濾掉的卡。 */
  function countVisibleCards() {
    return groups.reduce((sum, group) => {
      if (group.hidden) return sum;
      const cards = group.querySelectorAll(':scope > .search-results__grid > li');
      return sum + Array.from(cards).filter((li) => !li.hidden).length;
    }, 0);
  }

  /** 每組「查看全部 →」CTA：該組無任何命中卡時一併隱藏（避免空組掛懸空 CTA）。 */
  function syncGroupCtas() {
    groups.forEach((group) => {
      const more = group.querySelector(':scope > .search-results__more');
      if (!more) return;
      const cards = group.querySelectorAll(':scope > .search-results__grid > li');
      const anyVisible = Array.from(cards).some((li) => !li.hidden);
      more.hidden = !anyVisible;
    });
  }

  /** 計數行文案：q 空 → 清空（大框本身即提示打字）；非空 → 「找到 N 筆與「q」相關的結果」。
   *  須在 applyFilter 之後呼叫（countVisibleCards 依賴已套用的 li.hidden）。 */
  function syncSummary(q) {
    if (!summary) return;
    if (q === '') {
      summary.textContent = '';
    } else {
      // 命中卡數（已套關鍵字過濾；此時三組皆顯，等於跨組命中總數）。
      const total = countVisibleCards();
      // textContent 賦值 → q 即使含 < > & 也只當字面字元顯示，無 HTML 注入。
      summary.textContent = `找到 ${total} 筆與「${q}」相關的結果`;
    }
  }

  /** 空態標題只綁「實際搜尋的 q」，不殘留 HTML 種子的任何特定詞；q 空時回中性句。
   *  textContent 賦值 → q 即使含 < > & 也只當字面字元顯示，無 HTML 注入。 */
  function syncEmptyTitle(q) {
    if (!emptyTitle) return;
    emptyTitle.textContent = q === ''
      ? '找不到相關的結果'
      : `找不到與「${q}」相關的結果`;
  }

  /** 依目前可見卡數切換 empty-state 顯隱。 */
  function syncEmptyState() {
    if (!emptyEl) return;
    emptyEl.hidden = countVisibleCards() !== 0;
  }

  /* ===== 分頁同步（§7 功能化；取代靜態假頁碼）=====
   * 預覽模型：每類 3 卡 + 各自「查看全部 →」CTA，可見卡數恆 ≤ PAGE_SIZE → 單頁 → pager 清空。
   * 這是誠實行為（移除誤導的 1 2 3 4），非 bug。pager 仍接 buildPaginationItems：未來若單一 tab
   * 可見卡跨頁，會自動顯示真省略號分頁。此處只算頁數 + 上漆 + compact，不做跨 group 切片
   * （刻意不過度抽象：分面預覽各 group 有自己的 see-all 出口，非單一長列表）。 */
  const pagerEl = document.querySelector(SELECTORS.pager);
  function syncPager() {
    if (!pagerEl) return;
    const pageCount = Math.max(1, Math.ceil(countVisibleCards() / PAGE_SIZE));
    if (pageCount <= 1) {
      // 單頁 → pager 清空。同時 hidden 整個 <nav>，避免留下「具名但 0 子元素」的空 landmark（a11y 雜訊，S-16）。
      pagerEl.innerHTML = '';
      pagerEl.classList.remove('pagination--compact');
      pagerEl.hidden = true;
      return;
    }
    pagerEl.hidden = false; // 有真分頁才讓 landmark 進 a11y 樹。
    const compact = !!(COMPACT_MQ && COMPACT_MQ.matches);
    pagerEl.classList.toggle('pagination--compact', compact);
    const hrefFor = (p) => `?page=${p}`;
    pagerEl.innerHTML = compact
      ? renderCompact(1, pageCount, { mode: 'link', hrefFor })
      : buildPaginationItems(1, pageCount).map((it) => renderItemLink(it, hrefFor)).join('');
  }

  /** 單一重繪入口：過濾 → 計數行 / 空態標題 → 空組 CTA / 空態 / pager。
   *  載入時用 URL ?q= 呼叫（既有行為不變），輸入框打字時用 input.value 呼叫（即時過濾）。
   *  順序與原載入序一致：先 applyFilter 落定 li.hidden，文案與各 sync 才讀到正確命中數。 */
  function rerender(q) {
    applyFilter(q);
    syncSummary(q);
    syncEmptyTitle(q);
    syncGroupCtas();
    syncEmptyState();
    syncPager();
  }

  // 載入即同步：以 URL ?q= 跑一次完整重繪（過濾 + 文案 + 空組 CTA + 空態 + pager）。
  rerender(initialQ);

  // 斷點切換時重漆 pager（若有顯示）。
  const onMqChange = () => syncPager();
  if (COMPACT_MQ) {
    if (COMPACT_MQ.addEventListener) COMPACT_MQ.addEventListener('change', onMqChange);
    else if (COMPACT_MQ.addListener) COMPACT_MQ.addListener(onMqChange);
  }

  /* ===== 1b. 輸入框即時過濾（debounce 200ms）+ 同步 ?q= =====
   * 打字即過濾：debounce 200ms 後以 input.value 跑 rerender（同載入路徑，行為一致）。
   * URL 同步用 history.replaceState（不疊 history entry，免上一頁逐字回退）：q 非空 → ?q=，
   * q 空 → 退回乾淨 /search（去掉 ?q=）。原生表單 GET submit 行為保留（無 JS 或按 Enter 仍導頁）。 */
  let filterTimer = null;
  function onInput() {
    if (filterTimer) clearTimeout(filterTimer);
    filterTimer = setTimeout(() => {
      filterTimer = null;
      const q = input.value.trim();
      rerender(q);
      // 同步網址 ?q=（replaceState：不汙染 history、不觸發導頁/重載）。
      const url = q ? `${location.pathname}?q=${encodeURIComponent(q)}` : location.pathname;
      history.replaceState(null, '', url);
    }, 200);
  }
  if (input) input.addEventListener('input', onInput);

  /* ===== 2. tab 委派（選項 A，自寫）：切類型顯隱三 section ===== */
  function onTabClick(event) {
    const tab = event.target.closest(SELECTORS.tab);
    if (!tab || !tabsRoot.contains(tab)) return;

    const filter = tab.getAttribute('data-filter') || 'all';

    // 顯隱三 section：all → 全顯；否則只顯對應 id 的 section。
    groups.forEach((group) => {
      group.hidden = filter !== 'all' && group.id !== FILTER_TO_SECTION[filter];
    });

    // 切 active 視覺態 + a11y selected（is-active = 視覺，aria-selected = 必要 a11y）。
    tabs.forEach((t) => {
      const isActive = t === tab;
      t.classList.toggle('is-active', isActive);
      t.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    syncEmptyState();
    syncPager(); // 切 tab → 可見集合變 → 重算頁數（預覽模型恆單頁 → 維持清空）。
  }
  tabsRoot.addEventListener('click', onTabClick);

  /* ===== 7.1 空態「清除搜尋」：清空搜尋框 + 退回乾淨 /search + 重置回「全部」tab + 重繪 =====
   * 重用既有路徑（單一事實源）：先取消待觸發的 debounce、清 input.value、replaceState 去掉 ?q=，
   * 模擬點「全部」tab 走 onTabClick（還原三 section 顯示 + active 態），再 rerender('') 清關鍵字過濾
   * （還原全卡 + 清計數行 + 隱空態 + 重算 pager）。 */
  const clearBtn = document.querySelector('[data-search-clear]');
  const allTab = tabs.find((t) => (t.getAttribute('data-filter') || 'all') === 'all');
  let onClear = null;
  if (clearBtn) {
    onClear = () => {
      if (filterTimer) { clearTimeout(filterTimer); filterTimer = null; }
      if (input) input.value = '';
      history.replaceState(null, '', location.pathname);
      if (allTab) allTab.click();   // 還原三 section + tab active 態（走既有委派）
      rerender('');                  // 清關鍵字過濾 + 文案 + 空態 + pager
      if (input && typeof input.focus === 'function') input.focus();
    };
    clearBtn.addEventListener('click', onClear);
  }

  /* ===== cleanup（pagehide / bfcache）===== */
  function destroy() {
    tabsRoot.removeEventListener('click', onTabClick);
    if (input) input.removeEventListener('input', onInput);
    if (clearBtn && onClear) clearBtn.removeEventListener('click', onClear);
    if (filterTimer) { clearTimeout(filterTimer); filterTimer = null; }
    if (COMPACT_MQ) {
      if (COMPACT_MQ.removeEventListener) COMPACT_MQ.removeEventListener('change', onMqChange);
      else if (COMPACT_MQ.removeListener) COMPACT_MQ.removeListener(onMqChange);
    }
  }
  window.addEventListener('pagehide', destroy, { once: true });

  return { destroy };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
